import React, { type ReactElement } from 'react';
import I18n from 'i18n-js';
import type { FormattedString, TagType } from '../../types';
import { RCFormGroup } from '../RCFormGroup';

export interface OptionProps {
  label: string;
  value: string;
  disabled?: boolean;
}

/** An individual option within an RCSelect */
function Option({ value, disabled, label }: OptionProps) {
  return (
    <option
      disabled={disabled}
      value={value}
    >
      {label}
    </option>
  );
}

export interface OptionGroupProps {
  label?: string;
  /** Children must be `RCSelect.Option` */
  children?: ReactElement<OptionProps> | ReactElement<OptionProps>[];
}

/** Grouped options within an RCSelect */
function OptionGroup({ label, children }: OptionGroupProps) {
  return (
    <optgroup label={label}>
      {children}
    </optgroup>
  );
}

type SelectChild = ReactElement<OptionProps> | ReactElement<OptionGroupProps>;

interface Props {
  /** Main label text (or more complex components if you really need to) */
  label: FormattedString;

  /** ID of the input component, must be unique across the page */
  id: string;

  /** html attr, used by html form submission */
  name: string;

  disabled?: boolean;

  /** Signals to browser's form validator that a non-empty value must be selected */
  required?: boolean;

  /** Validation error message */
  errorText?: string;
  /** Additional info displayed below label */
  helpText?: FormattedString;
  /** Annotation on the field, e.g. 'required', 'optional' */
  tag?: TagType;

  /** called when value changes; updated value in evt.target.value */
  onChange: React.ChangeEventHandler<HTMLSelectElement>;

  /** called when input element loses focus */
  onBlur?: React.FocusEventHandler<HTMLSelectElement>;

  /** current value */
  value: string;

  /** List of options to display in the select. Must be `Option` or `OptionGroup` components */
  children: SelectChild | SelectChild[];

  /**
   * when true, prepends a disabled "Select One" option (can this be added when needed based on 'required' and 'value'?)
   */
  addEmptyOption?: boolean;

  /** when addEmptyOption is true, sets the empty option's label (defaults to translated 'commons.selectFormGroup.selectOne') */
  emptyOptionLabel?: string;
}

export function RCSelect(props: Props) {
  return (
    <RCFormGroup
      inputId={props.id}
      label={props.label}
      errorText={props.errorText}
      helpText={props.helpText}
      tag={props.tag}
    >
      <div className="rc-select">
        <select
          disabled={props.disabled}
          id={props.id}
          name={props.name}
          onBlur={props.onBlur}
          onChange={props.onChange}
          required={props.required}
          value={props.value}
        >
          {props.addEmptyOption &&
            <Option
              disabled
              value=""
              label={props.emptyOptionLabel || I18n.t('commons.selectFormGroup.selectOne')}
            />
          }
          {props.children}
        </select>
      </div>
    </RCFormGroup>
  );
}

RCSelect.Option = Option;
RCSelect.OptionGroup = OptionGroup;
